import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import {
  fetchStockById,
  updateInventoryStock
} from '../../../services/inventoryStockService';
import { fetchInventoryProducts } from '../../../services/inventoryProductsService';

interface Stock {
  stock_id?: number;
  product_id: number;
  location: string;
  quantity: number;
}

const EditStock: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); // Stock ID for editing

  const [formData, setFormData] = useState<Stock>({
    product_id: 0,
    location: '',
    quantity: 0
  });

  const [products, setProducts] = useState<
    { product_id: number; product_name: string }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch stock details if editing
  useEffect(() => {
    const loadStock = async () => {
      if (id) {
        setLoading(true);
        try {
          const stockData = await fetchStockById(id);
          setFormData({
            product_id: stockData.product_id,
            location: stockData.location,
            quantity: stockData.quantity
          });
        } catch (error) {
          setError('Failed to load stock details. Please try again.');
          console.error('Error fetching stock:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadStock();
  }, [id]);

  // Fetch products if adding new stock
  useEffect(() => {
    if (!id) {
      const loadProducts = async () => {
        try {
          const productList = await fetchInventoryProducts();
          setProducts(productList);
        } catch (error) {
          setError('Failed to load products. Please try again.');
          console.error('Error fetching products:', error);
        }
      };
      loadProducts();
    }
  }, [id]);

  // Handle input change
  // Handle input change for product fields
  const handleChange = (e: React.ChangeEvent<any>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    setFormData({ ...formData, [target.name]: target.value });
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await updateInventoryStock(id!, formData);
      alert('Stock updated successfully');
      navigate('/inventory/stock/all-stock');
    } catch (error) {
      setError('Error updating stock. Please try again.');
      console.error('Error updating stock:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container">
      <h2>{id ? 'Edit Stock' : 'Add Stock'}</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p>Loading stock details...</p>
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {/* Product Selection (only if adding new stock) */}
          {!id && (
            <Form.Group>
              <Form.Label>Select Product</Form.Label>
              <Form.Select
                name="product_id"
                value={formData.product_id}
                onChange={handleChange}
                required
              >
                <option value="">Select Product</option>
                {products.map(product => (
                  <option key={product.product_id} value={product.product_id}>
                    {product.product_name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {/* Location */}
          <Form.Control
            as="input" // Explicitly define the element type
            name="location"
            value={formData.location}
            onChange={handleChange}
            required
            disabled={submitting}
          />

          {/* Quantity */}
          <Form.Control
            as="input"
            type="number"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            required
            disabled={submitting}
          />

          {/* Submit Button */}
          <Button type="submit" className="mt-3" disabled={submitting}>
            {submitting ? (
              <Spinner animation="border" size="sm" />
            ) : id ? (
              'Update Stock'
            ) : (
              'Add Stock'
            )}
          </Button>
        </Form>
      )}
    </div>
  );
};

export default EditStock;
